import React, { useState, useEffect } from 'react'
import { X, Shield, AlertCircle } from 'lucide-react'
import { teamService } from '../team/teamService'
import { AdminUserItem } from '../../types/team'

interface ManagePermissionsModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (message: string) => void
  userItem: AdminUserItem | null
}

const AVAILABLE_ROLES = [
  { id: 'admin', label: 'Administrador', desc: 'Gestión técnica y de usuarios de la organización. Sin acceso clínico automático.' },
  { id: 'coach', label: 'Entrenador (Coach)', desc: 'Dirección deportiva, catálogo táctico, convocatorias y seguimiento.' },
  { id: 'assistant_coach', label: 'Entrenador Asistente / Preparador', desc: 'Captura de campo, consulta de estadísticas y carga física.' },
  { id: 'medic', label: 'Sanitario / Fisioterapeuta', desc: 'Acceso a salud privada, notas clínicas y disponibilidad operativa.' },
  { id: 'athlete', label: 'Atleta / Jugador', desc: 'Acceso a su perfil propio, formularios y estadísticas autorizadas.' },
]

const ALL_CAPABILITIES = [
  { id: 'view_performance_evaluations', label: 'Consultar evaluaciones de rendimiento', sensitive: false },
  { id: 'manage_performance_evaluations', label: 'Registrar/corregir evaluaciones de rendimiento (asignar consulta por separado)', sensitive: false },
  { id: 'view_body_measurements', label: 'Consultar medidas corporales', sensitive: true },
  { id: 'manage_body_measurements', label: 'Registrar/corregir medidas corporales (asignar consulta por separado)', sensitive: true },
  { id: 'manage_team_sports', label: 'Gestión deportiva del equipo', sensitive: false },
  { id: 'manage_roster', label: 'Gestión de jugadores y seguimiento', sensitive: false },
  { id: 'invite_athletes', label: 'Invitar atletas vinculados a ficha', sensitive: false },
  { id: 'manage_events', label: 'Convocar participantes y eventos', sensitive: false },
  { id: 'manage_playbook', label: 'Preparar catálogos tácticos, quintetos y guiones', sensitive: false },
  { id: 'capture_field', label: 'Capturar y corregir snaps en campo', sensitive: false },
  { id: 'view_stats', label: 'Consultar estadísticas colectivas', sensitive: false },
  { id: 'view_workload', label: 'Consultar carga deportiva agregada (sRPE)', sensitive: false },
  { id: 'view_form_compliance', label: 'Consultar cumplimiento de formularios', sensitive: false },
  { id: 'manage_health', label: 'Acceso sanitario y notas clínicas privadas', sensitive: true },
  { id: 'publish_availability', label: 'Publicar semáforo de disponibilidad operativa', sensitive: true },
  { id: 'manage_users', label: 'Administrar usuarios, roles y permisos', sensitive: true },
]

export const ManagePermissionsModal: React.FC<ManagePermissionsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userItem,
}) => {
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [selectedCapabilities, setSelectedCapabilities] = useState<string[]>([])
  const [isActive, setIsActive] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !userItem) {
      setErrorMsg(null)
      return
    }

    setSelectedRoles(userItem.roles || [])
    setSelectedCapabilities(userItem.capabilities || [])
    setIsActive(userItem.is_active ?? true)
    setErrorMsg(null)

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen, userItem, onClose])

  if (!isOpen || !userItem) return null

  const handleToggleRole = (roleId: string) => {
    if (selectedRoles.includes(roleId)) {
      setSelectedRoles(selectedRoles.filter((r) => r !== roleId))
    } else {
      setSelectedRoles([...selectedRoles, roleId])
    }
  }

  const handleToggleCapability = (capId: string) => {
    if (selectedCapabilities.includes(capId)) {
      setSelectedCapabilities(selectedCapabilities.filter((c) => c !== capId))
    } else {
      setSelectedCapabilities([...selectedCapabilities, capId])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)

    if (selectedRoles.length === 0 && isActive) {
      setErrorMsg('Debes asignar al menos un rol activo o marcar la cuenta como inactiva.')
      return
    }

    setIsSubmitting(true)
    try {
      const res = await teamService.manageUserPermissions({
        userId: userItem.user_id,
        organizationId: userItem.organization_id,
        teamId: userItem.team_id || '33333333-3333-3333-3333-333333333333',
        roles: selectedRoles,
        capabilities: selectedCapabilities,
        isActive,
      })

      if (res.success) {
        onSuccess(`Permisos de ${userItem.full_name} actualizados exitosamente.`)
        onClose()
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al guardar permisos.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="modal-content"
        style={{ maxWidth: '560px', width: '92vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Shield size={18} style={{ color: 'var(--brand-red)' }} />
            <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
              Gestionar Permisos y Capacidades
            </h2>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            aria-label="Cerrar modal"
            style={{ padding: '0.25rem' }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
            {errorMsg && (
              <div
                style={{
                  padding: '0.75rem',
                  backgroundColor: 'var(--status-danger-bg)',
                  color: 'var(--status-danger-text)',
                  border: '1px solid var(--status-danger-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.82rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                }}
              >
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>{errorMsg}</div>
              </div>
            )}

            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-main)' }}>
                {userItem.full_name}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                {userItem.email} · Equipo: {userItem.team_name || 'Organización'}
              </div>
            </div>

            {/* Estado de acceso */}
            <div
              style={{
                padding: '0.75rem',
                backgroundColor: 'var(--surface-subtle)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <span style={{ fontSize: '0.82rem', fontWeight: 600, display: 'block' }}>
                  Acceso a la Plataforma
                </span>
                <span style={{ fontSize: '0.73rem', color: 'var(--text-secondary)' }}>
                  {isActive ? 'Usuario activo con acceso autorizado' : 'Acceso revocado temporalmente'}
                </span>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <span>{isActive ? 'Activo' : 'Revocado'}</span>
              </label>
            </div>

            {/* Roles asignados */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.4rem' }}>
                Roles del Usuario
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                {AVAILABLE_ROLES.map((r) => {
                  const isChecked = selectedRoles.includes(r.id)
                  return (
                    <label
                      key={r.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.5rem',
                        padding: '0.55rem 0.65rem',
                        border: isChecked ? '1px solid var(--brand-blue)' : '1px solid var(--surface-border)',
                        borderRadius: 'var(--radius-sm)',
                        backgroundColor: isChecked ? 'rgba(37, 99, 235, 0.04)' : 'transparent',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleRole(r.id)}
                        style={{ marginTop: '2px' }}
                      />
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{r.label}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{r.desc}</div>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Capacidades granulares adicionales */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, margin: 0 }}>
                  Capacidades Efectivas Específicas
                </label>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                  Añadidas a los roles base
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.35rem' }}>
                {ALL_CAPABILITIES.map((cap) => {
                  const isChecked = selectedCapabilities.includes(cap.id)
                  return (
                    <label
                      key={cap.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.45rem 0.6rem',
                        border: '1px solid var(--surface-border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.76rem',
                        backgroundColor: cap.sensitive ? 'rgba(239, 68, 68, 0.02)' : 'transparent',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleCapability(cap.id)}
                        />
                        <span style={{ fontWeight: isChecked ? 600 : 400 }}>{cap.label}</span>
                      </div>

                      {cap.sensitive && (
                        <span
                          className="badge badge-red"
                          style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem' }}
                          title="Capacidad sensible protegida por RLS estricto"
                        >
                          Sensible
                        </span>
                      )}
                    </label>
                  )
                })}
              </div>
            </div>
          </div>

          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.75rem 1rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : 'Guardar Permisos'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
